// slices/serviceProviderReviewSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ServiceProviderReviewService from "../services/ServiceProviderReviewService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

/**
 * Get all reviews for a provider with pagination
 * GET /api/service-providers/:providerId/reviews
 */
export const getReviews = createAsyncThunk(
  'serviceProviderReview/getReviews',
  async ({ providerId, page = 1, limit = 10, sortBy = 'date', sortOrder = 'desc' }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.getReviews(providerId, page, limit, sortBy, sortOrder);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get review statistics for a provider
 * GET /api/service-providers/:providerId/reviews/statistics
 */
export const getReviewStatistics = createAsyncThunk(
  'serviceProviderReview/getReviewStatistics',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.getReviewStatistics(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get reviews by rating filter
 * GET /api/service-providers/:providerId/reviews/rating/:rating
 */
export const getReviewsByRating = createAsyncThunk(
  'serviceProviderReview/getReviewsByRating',
  async ({ providerId, rating, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.getReviewsByRating(providerId, rating, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get review trends (monthly statistics)
 * GET /api/service-providers/:providerId/reviews/trends
 */
export const getReviewTrends = createAsyncThunk(
  'serviceProviderReview/getReviewTrends',
  async ({ providerId, months = 6 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.getReviewTrends(providerId, months);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get provider rating summary for public display
 * GET /api/service-providers/:providerId/reviews/summary
 */
export const getRatingSummary = createAsyncThunk(
  'serviceProviderReview/getRatingSummary',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.getRatingSummary(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PROTECTED ROUTES --------

/**
 * Add a review to a service provider
 * POST /api/service-providers/:providerId/reviews
 */
export const addReview = createAsyncThunk(
  'serviceProviderReview/addReview',
  async ({ providerId, reviewData }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.addReview(providerId, reviewData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Flag a review as inappropriate
 * POST /api/service-providers/:providerId/reviews/:reviewId/flag
 */
export const flagReview = createAsyncThunk(
  'serviceProviderReview/flagReview',
  async ({ providerId, reviewId, reason }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.flagReview(providerId, reviewId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ADMIN ROUTES --------

/**
 * Update a review (Admin only)
 * PUT /api/service-providers/:providerId/reviews/:reviewId
 */
export const updateReview = createAsyncThunk(
  'serviceProviderReview/updateReview',
  async ({ providerId, reviewId, updateData }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.updateReview(providerId, reviewId, updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete a review (Admin only)
 * DELETE /api/service-providers/:providerId/reviews/:reviewId
 */
export const deleteReview = createAsyncThunk(
  'serviceProviderReview/deleteReview',
  async ({ providerId, reviewId }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderReviewService.deleteReview(providerId, reviewId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Review data
  reviews: [],
  reviewStatistics: null,
  reviewsByRating: [],
  reviewTrends: [],
  ratingSummary: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Selected review
  selectedReview: null,
  
  // Loading states
  loading: false,
  loadingStatistics: false,
  loadingTrends: false,
  submitting: false,
  updating: false,
  deleting: false,
  
  // Error states
  error: null,
  submitError: null,
  updateError: null,
  deleteError: null,
  
  // Success states
  submitSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
  flagSuccess: false,
  
  // Filter states
  filterRating: null,
  sortBy: 'date',
  sortOrder: 'desc',
  
  // UI states
  showAllReviews: false,
  expandedReviewId: null,
};

const serviceProviderReviewSlice = createSlice({
  name: 'serviceProviderReview',
  initialState,
  reducers: {
    // Reset states
    resetReviewState: (state) => {
      state.reviews = [];
      state.reviewStatistics = null;
      state.reviewsByRating = [];
      state.reviewTrends = [];
      state.ratingSummary = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.selectedReview = null;
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.deleteError = null;
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
      state.flagSuccess = false;
      state.filterRating = null;
      state.showAllReviews = false;
      state.expandedReviewId = null;
    },
    
    // Clear errors
    clearReviewErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    // Clear success states
    clearReviewSuccess: (state) => {
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
      state.flagSuccess = false;
    },
    
    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set filter rating
    setFilterRating: (state, action) => {
      state.filterRating = action.payload;
    },
    
    // Set sort options
    setSortOptions: (state, action) => {
      state.sortBy = action.payload.sortBy || state.sortBy;
      state.sortOrder = action.payload.sortOrder || state.sortOrder;
    },
    
    // Set selected review
    setSelectedReview: (state, action) => {
      state.selectedReview = action.payload;
    },
    
    // Toggle show all reviews
    toggleShowAllReviews: (state) => {
      state.showAllReviews = !state.showAllReviews;
    },
    
    // Set expanded review
    setExpandedReview: (state, action) => {
      state.expandedReviewId = action.payload;
    },
    
    // Update review locally (optimistic update)
    updateLocalReview: (state, action) => {
      const { reviewId, updateData } = action.payload;
      const reviewIndex = state.reviews.findIndex(r => r._id === reviewId);
      if (reviewIndex !== -1) {
        state.reviews[reviewIndex] = { ...state.reviews[reviewIndex], ...updateData };
      }
    },
    
    // Remove review locally (optimistic delete)
    removeLocalReview: (state, action) => {
      const reviewId = action.payload;
      state.reviews = state.reviews.filter(r => r._id !== reviewId);
    },
    
    // Add review locally (optimistic add)
    addLocalReview: (state, action) => {
      state.reviews.unshift(action.payload);
    },
    
    // Reset pagination
    resetPagination: (state) => {
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== GET REVIEWS ==========
      .addCase(getReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload.data.reviews || [];
        state.pagination = action.payload.data.pagination || state.pagination;
        state.reviewStatistics = {
          averageRating: action.payload.data.averageRating,
          totalReviews: action.payload.data.totalReviews,
          ratingDistribution: action.payload.data.ratingDistribution
        };
      })
      .addCase(getReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get reviews';
      })
      
      // ========== GET REVIEW STATISTICS ==========
      .addCase(getReviewStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getReviewStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.reviewStatistics = action.payload.data;
      })
      .addCase(getReviewStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get review statistics';
      })
      
      // ========== GET REVIEWS BY RATING ==========
      .addCase(getReviewsByRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReviewsByRating.fulfilled, (state, action) => {
        state.loading = false;
        state.reviewsByRating = action.payload.data.reviews || [];
        state.pagination = action.payload.data.pagination || state.pagination;
      })
      .addCase(getReviewsByRating.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get reviews by rating';
      })
      
      // ========== GET REVIEW TRENDS ==========
      .addCase(getReviewTrends.pending, (state) => {
        state.loadingTrends = true;
        state.error = null;
      })
      .addCase(getReviewTrends.fulfilled, (state, action) => {
        state.loadingTrends = false;
        state.reviewTrends = action.payload.data || [];
      })
      .addCase(getReviewTrends.rejected, (state, action) => {
        state.loadingTrends = false;
        state.error = action.payload || 'Failed to get review trends';
      })
      
      // ========== GET RATING SUMMARY ==========
      .addCase(getRatingSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRatingSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.ratingSummary = action.payload.data;
      })
      .addCase(getRatingSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get rating summary';
      })
      
      // ========== ADD REVIEW ==========
      .addCase(addReview.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(addReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.reviews.unshift(action.payload.data.review);
        if (state.reviewStatistics) {
          state.reviewStatistics.totalReviews += 1;
          state.reviewStatistics.averageRating = action.payload.data.newRating;
        }
      })
      .addCase(addReview.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to add review';
        state.submitSuccess = false;
      })
      
      // ========== FLAG REVIEW ==========
      .addCase(flagReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.flagSuccess = false;
      })
      .addCase(flagReview.fulfilled, (state) => {
        state.loading = false;
        state.flagSuccess = true;
      })
      .addCase(flagReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to flag review';
        state.flagSuccess = false;
      })
      
      // ========== UPDATE REVIEW (ADMIN) ==========
      .addCase(updateReview.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const updatedReview = action.payload.data.review;
        const reviewIndex = state.reviews.findIndex(r => r._id === updatedReview._id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex] = updatedReview;
        }
        if (state.reviewStatistics) {
          state.reviewStatistics.averageRating = action.payload.data.newRating;
        }
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update review';
        state.updateSuccess = false;
      })
      
      // ========== DELETE REVIEW (ADMIN) ==========
      .addCase(deleteReview.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.deleting = false;
        state.deleteSuccess = true;
        const { reviewId } = action.meta.arg;
        state.reviews = state.reviews.filter(r => r._id !== reviewId);
        if (state.reviewStatistics) {
          state.reviewStatistics.totalReviews -= 1;
          state.reviewStatistics.averageRating = action.payload.data.newRating;
        }
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to delete review';
        state.deleteSuccess = false;
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectReviewState = (state) => state.serviceProviderReview;

// Review data selectors
export const selectReviews = createSelector(
  selectReviewState,
  (review) => review.reviews
);

export const selectReviewStatistics = createSelector(
  selectReviewState,
  (review) => review.reviewStatistics
);

export const selectReviewsByRating = createSelector(
  selectReviewState,
  (review) => review.reviewsByRating
);

export const selectReviewTrends = createSelector(
  selectReviewState,
  (review) => review.reviewTrends
);

export const selectRatingSummary = createSelector(
  selectReviewState,
  (review) => review.ratingSummary
);

export const selectSelectedReview = createSelector(
  selectReviewState,
  (review) => review.selectedReview
);

// Pagination selectors
export const selectPagination = createSelector(
  selectReviewState,
  (review) => review.pagination
);

// Loading selectors
export const selectReviewsLoading = createSelector(
  selectReviewState,
  (review) => review.loading
);

export const selectStatisticsLoading = createSelector(
  selectReviewState,
  (review) => review.loadingStatistics
);

export const selectTrendsLoading = createSelector(
  selectReviewState,
  (review) => review.loadingTrends
);

export const selectSubmittingReview = createSelector(
  selectReviewState,
  (review) => review.submitting
);

export const selectUpdatingReview = createSelector(
  selectReviewState,
  (review) => review.updating
);

export const selectDeletingReview = createSelector(
  selectReviewState,
  (review) => review.deleting
);

// Error selectors
export const selectReviewError = createSelector(
  selectReviewState,
  (review) => review.error
);

export const selectSubmitReviewError = createSelector(
  selectReviewState,
  (review) => review.submitError
);

export const selectUpdateReviewError = createSelector(
  selectReviewState,
  (review) => review.updateError
);

export const selectDeleteReviewError = createSelector(
  selectReviewState,
  (review) => review.deleteError
);

// Success selectors
export const selectSubmitReviewSuccess = createSelector(
  selectReviewState,
  (review) => review.submitSuccess
);

export const selectUpdateReviewSuccess = createSelector(
  selectReviewState,
  (review) => review.updateSuccess
);

export const selectDeleteReviewSuccess = createSelector(
  selectReviewState,
  (review) => review.deleteSuccess
);

export const selectFlagReviewSuccess = createSelector(
  selectReviewState,
  (review) => review.flagSuccess
);

// Filter selectors
export const selectFilterRating = createSelector(
  selectReviewState,
  (review) => review.filterRating
);

export const selectSortOptions = createSelector(
  selectReviewState,
  (review) => ({ sortBy: review.sortBy, sortOrder: review.sortOrder })
);

// UI selectors
export const selectShowAllReviews = createSelector(
  selectReviewState,
  (review) => review.showAllReviews
);

export const selectExpandedReviewId = createSelector(
  selectReviewState,
  (review) => review.expandedReviewId
);

// Derived selectors
export const selectAverageRating = createSelector(
  [selectReviewStatistics, selectRatingSummary],
  (statistics, summary) => {
    if (statistics?.averageRating) return statistics.averageRating;
    if (summary?.averageRating) return summary.averageRating;
    return 0;
  }
);

export const selectTotalReviews = createSelector(
  [selectReviewStatistics, selectRatingSummary],
  (statistics, summary) => {
    if (statistics?.totalReviews) return statistics.totalReviews;
    if (summary?.totalReviews) return summary.totalReviews;
    return 0;
  }
);

export const selectRatingDistribution = createSelector(
  [selectReviewStatistics, selectRatingSummary],
  (statistics, summary) => {
    if (statistics?.ratingDistribution) return statistics.ratingDistribution;
    if (summary?.ratingCounts) return summary.ratingCounts;
    return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  }
);

export const selectHasReviews = createSelector(
  selectTotalReviews,
  (total) => total > 0
);

export const selectTopReviews = createSelector(
  selectReviews,
  (reviews) => {
    if (!reviews || reviews.length === 0) return [];
    return [...reviews]
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 3);
  }
);

export const selectRecentReviews = createSelector(
  selectReviews,
  (reviews) => {
    if (!reviews || reviews.length === 0) return [];
    return [...reviews]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  }
);

export const selectReviewsByStarRating = createSelector(
  [selectReviews, (state, rating) => rating],
  (reviews, rating) => {
    if (!reviews) return [];
    return reviews.filter(r => Math.round(r.rating) === rating);
  }
);

// ==================== EXPORTS ====================

export const {
  resetReviewState,
  clearReviewErrors,
  clearReviewSuccess,
  setPagination,
  setFilterRating,
  setSortOptions,
  setSelectedReview,
  toggleShowAllReviews,
  setExpandedReview,
  updateLocalReview,
  removeLocalReview,
  addLocalReview,
  resetPagination,
} = serviceProviderReviewSlice.actions;

export default serviceProviderReviewSlice.reducer;